import React, { useRef, useState, useEffect } from 'react';
import './CustomFileUpload.css';
import { LoadingSpinner } from '../LoadingSpinner/LoadingSpinner';
import ApiManager from '../../ApiManager/ApiManager';
import { toast } from 'react-toastify';

const CustomFileUpload = ({
  name,
  onChange,
  value,
  required = false,
  accept,
  label,
  error,
  isDisabled = false,
  className = '',
  isUploading = false,
  uploadProgress = 0,
  storedFileName = '', // The actual file name stored in the backend
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [fileName, setFileName] = useState(value || '');
  const [isViewLoading, setIsViewLoading] = useState(false);
  const fileInputRef = useRef(null);

  // Update fileName when value prop changes
  useEffect(() => {
    setFileName(value || '');
  }, [value]);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleFileSelect = (file) => {
    if (file) {
      setFileName(file.name);
      if (onChange) {
        onChange({
          target: {
            name,
            type: 'file',
            files: [file]
          }
        });
      }
    }
  };

  const handleClick = () => {
    if (!isDisabled) {
      fileInputRef.current?.click();
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Set the file name for display
      setFileName(file.name);

      // Just pass the file to the parent component without uploading
      onChange(e);
    }
  };

  // File deletion is now handled by the parent component

  const handleRemove = (e) => {
    e.stopPropagation();

    setFileName('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (onChange) {
      onChange({
        target: {
          name,
          type: 'file',
          files: [],
          action: 'remove',
          storedFileName: storedFileName
        }
      });
    }
  };

  const handleViewFile = async (e) => {
    e.stopPropagation();
    if (!storedFileName) return;

    try {
      setIsViewLoading(true);

      // Get a presigned URL for direct download
      const response = await ApiManager.storageGetPresignedDownloadUrl(storedFileName);

      if (!response.ok) {
        throw new Error("Failed to generate presigned URL");
      }

      const result = await response.json();

      // The response contains: url, fileName, expires
      console.log("Presigned download URL data:", result);

      // Open the presigned URL directly in a new tab
      window.open(result.url, "_blank");
      console.log("Download link will expire in:", result.expires);
    } catch (error) {
      console.error("Error fetching file:", error);
      toast.error("Failed to download file. Please try again.");
    } finally {
      setIsViewLoading(false);
    }
  };

  return (
    <div className={`custom-file-upload ${className} ${isDisabled ? 'disabled' : ''}`}>
      {label && <label htmlFor={name}>{label}</label>}
      <div
        className={`upload-area ${isDragging ? 'dragging' : ''} ${fileName ? 'has-file' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
      >
        <input
          ref={fileInputRef}
          type="file"
          id={name}
          name={name}
          onChange={handleFileChange}
          required={required}
          accept={accept}
          disabled={isDisabled || isUploading}
          style={{ display: 'none' }}
        />

        <div className="file-name">
          {fileName ? (
            <div className="file-info">
              <span className="file-name">{fileName}</span>
              <div className="file-actions">
                {storedFileName && (
                  <button
                    className="view-file"
                    onClick={handleViewFile}
                    type="button"
                    title="View file"
                    disabled={isViewLoading || isUploading}
                  >
                    {isViewLoading ? (
                      <LoadingSpinner size="small" />
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                        <circle cx="12" cy="12" r="3"></circle>
                      </svg>
                    )}
                  </button>
                )}
                <button
                  className="remove-file"
                  onClick={handleRemove}
                  type="button"
                  title="Remove file"
                  disabled={isUploading}
                >
                  {isUploading ? <LoadingSpinner size="small" /> : "×"}
                </button>
              </div>
            </div>
          ) : (
            <div className="upload-placeholder">
              <i className="upload-icon">📁</i>
              <span>Choose file</span>
            </div>
          )}
        </div>
        {isUploading && (
          <div className="upload-progress">
            <LoadingSpinner size="small" />
            <span>{uploadProgress}%</span>
          </div>
        )}
      </div>
      {error && <div className="error-message">{error}</div>}
    </div>
  );
};

export default CustomFileUpload;